import { useEffect, useLayoutEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainContainer from "../../../components/container/MainContainer";
import AddResource from "../../../components/header/AddResource";
import { useForm } from "react-hook-form";
import TextAreaField from "../../../components/form/TextAreaField";
import InputField from "../../../components/form/InputField";
import {
  addProblemStatement,
  getProject,
} from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import FilesUpload from "../../../components/form/FilesUpload";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  addProblemStatementShema,
  addProblemStatementShema2,
} from "../../../schemas";
import EditorField from "../../../components/form/EditorField";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";

const AddProblemStatement = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const problemStatLoad = useSelector(
    (state) => state.projects.load.addProblemStatement
  );
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(
      project.supporting_docs
        ? addProblemStatementShema2
        : addProblemStatementShema
    ),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    try {
      const res = await dispatch(addProblemStatement(data));
      if (addProblemStatement.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/requests/problem-statement-summary/" + id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "success");
    }
  };

  useLayoutEffect(() => {
    if (!project.id) {
      dispatch(getProject(id));
    }
  }, []);

  useEffect(() => {
    if (project.id) {
      reset({
        analysis: project.analysis,
        problem_statement: project.problem_statement,
        proposed_solution: project.proposed_solution,
      });
    }
  }, [project]);

  if (
    (id && project.id && project.stage.step_number < 1) ||
    (id && project.id && project.stage.step_number > 3)
  ) {
    return navigate("/requests/my-requests");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <AddResource title="Project Request - Need Assessment " />
        {project && (
          <form
            onSubmit={(e) => handleSubmit(onSubmit)(e)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Problem Statement</h5>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              <EditorField
                setValue={setValue}
                register={register}
                name="analysis"
                label="Analysis of Existing Situation"
                defaultValue={project.analysis || null}
                errors={errors.analysis}
              />
              <EditorField
                setValue={setValue}
                register={register}
                name="problem_statement"
                label="Problem Statement"
                defaultValue={project.problem_statement || null}
                errors={errors.problem_statement}
              />
              <EditorField
                setValue={setValue}
                register={register}
                name="proposed_solution"
                label="Proposed Solution"
                defaultValue={project.proposed_solution || null}
                errors={errors.proposed_solution}
              />
              {project.supporting_docs && (
                <div>
                  <Link
                    className="hover:text-primary text-primary/85 "
                    to={project.supporting_docs}
                    target="_blank"
                  >
                    Existing Supporting Documents Link
                  </Link>
                </div>
              )}
              <FilesUpload
                register={register}
                name="supporting_docs"
                label="Supporting Documents"
                errors={errors.supporting_docs}
                setValue={setValue}
                watch={watch}
                accept="application/pdf"
              />

              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    // dispatch(unsetSelectedProject());
                    navigate("/requests/beneficiary-profile/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={problemStatLoad}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={problemStatLoad}
                  className="add-rss-btn"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </MainContainer>
  );
};

export default AddProblemStatement;
